/** Modals the domain screens share: choosing or adding a card, and confirming who you are. */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CreditCard } from "lucide-react";
import { useCallback, useId, useRef, useState, type ReactNode } from "react";

import { useOrganization } from "@/auth/organization";
import { useUser } from "@/auth/session";
import { CardFields, emptyCard, type CardForm } from "@/components/billing/CardFields";
import { PayIcon } from "@/components/billing/PayIcon";
import {
  PAY_METHOD_LABELS,
  PaymentMethodSelector,
  type PayMethod,
} from "@/components/billing/PaymentMethodSelector";
import { SaveCardChoice, type SaveChoice } from "@/components/billing/SaveCardChoice";
import { PoweredByStripe } from "@/components/billing/PoweredByStripe";
import {
  ErrorAlert,
  Form,
  PasswordInput,
  cx,
  Modal,
  Field,
  Input,
  CONTROL_LG,
  Button,
  Toggle,
} from "@/components/ui";
import { auth, identity, portal, session } from "@/lib/api";
import { ApiError } from "@/lib/http";
import { payableDefault, payableNow, processorName, type CardErrors } from "@/lib/payments";
import { supportsApplePay, supportsGooglePay, type Wallet } from "@/lib/stripe";
import { useCardEntry } from "@/components/billing/useCardEntry";
import type { PaymentMethod } from "@/lib/types";
import { Hint } from "@/components/domains/parts";

export function usePaymentMethods() {
  const { orgId } = useOrganization();
  return useQuery({ queryKey: ["payment-methods", orgId], queryFn: () => portal.paymentMethods(orgId) });
}

/** The card a domain payment starts on (a saved Apple Pay card is kept for renewals, so it's skipped). */
export function defaultMethod(methods: PaymentMethod[] | undefined): PaymentMethod | undefined {
  return payableDefault(methods);
}

/** The "VISA" chip next to a saved card. */
export function CardBadge({ brand }: { brand: string }) {
  return (
    <span className="flex h-[26px] w-[38px] shrink-0 items-center justify-center rounded-md bg-[#f3ebe7] text-[9px] font-extrabold tracking-wide text-[#1a1f71] italic">
      {brand === "visa" ? (
        "VISA"
      ) : brand === "mastercard" ? (
        "MC"
      ) : brand === "amex" ? (
        "AMEX"
      ) : (
        <CreditCard className="size-3.5 text-fg-muted" />
      )}
    </span>
  );
}

/**
 * Figma "Update payment method": pick a card on file or add one (the bank checks it, nothing is
 * charged). `onChosen` gets the card to use.
 *
 * `oneTime` (domain purchases and renewals): a new card comes with an explicit "save this card for
 * future payments?" choice. Not saving gives a one-time card (`saved: false`) that pays for this
 * domain payment only; the renewal-risk warning shows as soon as the person opts out.
 */
export function PaymentMethodModal({
  open,
  onClose,
  onChosen,
  selectedId,
  title = "Update payment method",
  oneTime = false,
  onWallet,
}: {
  open: boolean;
  onClose: () => void;
  onChosen: (method: PaymentMethod) => void;
  selectedId?: string | null;
  title?: string;
  oneTime?: boolean;
  /** The domain checkout: a new method can also be Apple Pay or Google Pay (the total is approved in
   * the wallet's sheet when the person completes the purchase). */
  onWallet?: (choice: WalletChoice) => void;
}) {
  const { orgId } = useOrganization();
  const user = useUser();
  const qc = useQueryClient();
  const methods = usePaymentMethods();
  const usable = (methods.data?.payment_methods ?? []).filter(payableNow);
  const [adding, setAdding] = useState(false);
  const [picked, setPicked] = useState<string | null>(selectedId ?? null);
  const [card, setCard] = useState<CardForm>(emptyCard(user.display_name ?? ""));
  const [errors, setErrors] = useState<CardErrors>({});
  const [makeDefault, setMakeDefault] = useState(true);
  // Nothing pre-ticked: where a card may be used once, the person says whether to keep it.
  const [keep, setKeep] = useState<SaveChoice>(oneTime ? null : "save");
  const [keepError, setKeepError] = useState<string | null>(null);
  // Google Pay has its own tab where wallets are offered, so it stays out of the card form there.
  const entry = useCardEntry(orgId, { googlePay: !onWallet });
  const showForm = adding || (methods.isSuccess && usable.length === 0);
  const saving = keep !== "once";
  const [tab, setTab] = useState<PayMethod>("card");
  const wallet: Wallet | null = onWallet && (tab === "apple_pay" || tab === "google_pay") ? tab : null;
  const walletHere =
    methods.data?.provider === "stripe" &&
    (wallet === "apple_pay" ? supportsApplePay() : supportsGooglePay());

  const choose = (next: Exclude<SaveChoice, null>) => {
    setKeep(next);
    setKeepError(null);
    entry.showSaveTerms(next === "save");
  };

  const add = useMutation({
    mutationFn: () => entry.addCard(card, { makeDefault: saving && makeDefault, save: saving }),
    onSuccess: async (method) => {
      await qc.invalidateQueries({ queryKey: ["payment-methods", orgId] });
      setAdding(false);
      setCard(emptyCard(user.display_name ?? ""));
      setKeep(oneTime ? null : "save");
      onChosen(method);
    },
  });

  const save = async () => {
    if (showForm) {
      if (keep === null) {
        setKeepError("Choose whether to save this card before you continue.");
        return;
      }
      if (wallet && onWallet) {
        // Nothing to check yet: the wallet's sheet asks for approval when the purchase is completed.
        onWallet({ wallet, save: saving, makeDefault: saving && makeDefault });
        setKeep(oneTime ? null : "save");
        return;
      }
      const found = await entry.validate(card);
      setErrors(found);
      if (!Object.keys(found).length) add.mutate();
      return;
    }
    const method = usable.find((m) => m.id === (picked ?? defaultMethod(usable)?.id));
    if (method) onChosen(method);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      subtitle="Used for your subscription, domain renewals and mailboxes"
      note="Your card is encrypted and processed securely."
      footer={
        <>
          <Button
            variant="secondary"
            size="lg"
            onClick={showForm && usable.length ? () => setAdding(false) : onClose}
          >
            {showForm && usable.length ? "Back" : "Cancel"}
          </Button>
          <Button
            variant="brand"
            size="lg"
            onClick={() => void save()}
            loading={add.isPending}
            disabledReason={
              showForm && wallet !== null && !walletHere
                ? `${PAY_METHOD_LABELS[wallet]} isn't available in this browser. Choose a card instead.`
                : undefined
            }
          >
            {!showForm
              ? "Use this card"
              : wallet
                ? `Use ${PAY_METHOD_LABELS[wallet]}`
                : saving
                  ? "Save card"
                  : "Use for this payment"}
          </Button>
        </>
      }
    >
      {!showForm && (
        <div className="flex flex-col gap-2">
          {usable.map((m) => {
            const active = (picked ?? defaultMethod(usable)?.id) === m.id;
            return (
              <Button
                unstyled
                key={m.id}
                type="button"
                onClick={() => setPicked(m.id)}
                className={cx(
                  "flex items-center gap-3 rounded-xl border p-3 text-left",
                  active ? "border-brand-500 bg-brand-50" : "border-border hover:bg-muted",
                )}
              >
                <CardBadge brand={m.brand} />
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-semibold text-fg">{m.label}</span>
                  <span className="block text-xs text-fg-subtle">
                    Expires {String(m.exp_month).padStart(2, "0")}/{String(m.exp_year).slice(-2)}
                    {m.is_default ? " · Default" : ""}
                  </span>
                </span>
                <span
                  className={cx(
                    "size-4 rounded-full border-2",
                    active
                      ? "border-brand-500 bg-brand-500 shadow-[inset_0_0_0_2px_white]"
                      : "border-[#d4cac4]",
                  )}
                />
              </Button>
            );
          })}
          <Button
            unstyled
            type="button"
            onClick={() => setAdding(true)}
            className="self-start text-sm font-semibold text-brand-500 hover:underline"
          >
            {onWallet ? "+ Add a new payment method" : "+ Add a new card"}
          </Button>
        </div>
      )}
      {showForm && (
        <>
          {onWallet && (
            // No bank debit here: a Direct Debit takes days to clear, so it can't pay for an order now.
            <PaymentMethodSelector value={tab} onChange={setTab} methods={WINDOW_METHODS} compact />
          )}
          {wallet ? (
            <WalletTabNote
              wallet={wallet}
              here={walletHere}
              connected={methods.data?.provider === "stripe"}
            />
          ) : (
            <CardFields entry={entry} value={card} onChange={setCard} errors={errors} />
          )}
          {oneTime && (
            <SaveCardChoice
              value={keep}
              onChange={choose}
              processor={processorName(entry.provider)}
              error={keepError}
            />
          )}
          {keep === "save" && (
            <div className="flex items-center gap-3">
              <div className="min-w-0 flex-1">
                <p className="text-[13px] font-semibold text-fg">Set as default payment method</p>
                <p className="text-[11px] text-fg-subtle">Used for all future charges across CortexGrip</p>
              </div>
              <Toggle
                size="lg"
                checked={makeDefault}
                onChange={setMakeDefault}
                label="Set as default payment method"
              />
            </div>
          )}
        </>
      )}
      <ErrorAlert error={add.error} />
      <PoweredByStripe provider={methods.data?.provider} />
    </Modal>
  );
}

/** A wallet chosen in the domain checkout's payment window: paid in its sheet on "Complete purchase". */
/** What the domain checkout's payment window offers for a new payment method. */
const WINDOW_METHODS: PayMethod[] = ["card", "apple_pay", "google_pay"];

export interface WalletChoice {
  wallet: Wallet;
  /** Keep its card for future payments (Apple Pay: renewals only) or use it for this payment only. */
  save: boolean;
  makeDefault: boolean;
}

/** The Apple Pay / Google Pay tab of the payment window. No Figma frame: it follows the card tab. */
function WalletTabNote({ wallet, here, connected }: { wallet: Wallet; here: boolean; connected: boolean }) {
  const name = PAY_METHOD_LABELS[wallet];
  return (
    <p role="status" className="flex items-start gap-3 rounded-xl bg-[#faf6ef] p-4 text-sm text-fg-muted">
      <PayIcon method={wallet} className="mt-0.5 size-[18px]" />
      <span className="min-w-0">
        {!connected
          ? `${name} isn't switched on yet. Use a card instead.`
          : !here
            ? wallet === "apple_pay"
              ? "Apple Pay works in Safari on an iPhone, iPad or Mac with a card in Apple Wallet. Use a card here instead."
              : "Google Pay needs a secure (https) page. Use a card here instead."
            : `When you complete the purchase, ${name} shows the total and you approve it there.`}
        {connected && here && wallet === "apple_pay" && (
          <span className="mt-1 block text-xs text-[#766e6a]">
            Saved, Apple Pay is kept for your domain and plan renewals; buying something later uses a card.
          </span>
        )}
      </span>
    </p>
  );
}

// ── step-up sign-in ─────────────────────────────────────────────────────────
/**
 * Figma "Confirm it's you": the password (and 2FA code) again before actions that could hand the
 * domain to someone else. It signs in again with Identity, so the API sees a fresh sign-in.
 */
export function ConfirmIdentityModal({
  open,
  onClose,
  onConfirmed,
  subtitle,
  confirmLabel = "Verify & continue",
  children,
}: {
  open: boolean;
  onClose: () => void;
  onConfirmed: () => void;
  subtitle?: ReactNode;
  confirmLabel?: string;
  children?: ReactNode;
}) {
  const user = useUser();
  const me = useQuery({ queryKey: ["me"], queryFn: identity.me, enabled: open });
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const verify = useMutation({
    mutationFn: async () => {
      const first = await auth.login(user.email, password);
      const tokens =
        "status" in first && first.status === "mfa_required"
          ? await auth.loginMfa((first as { challenge_token: string }).challenge_token, { code: code.trim() })
          : first;
      session.accept(tokens as Parameters<typeof session.accept>[0]);
    },
    onSuccess: () => {
      setPassword("");
      setCode("");
      onConfirmed();
    },
  });
  const mfa = me.data?.mfa_enabled ?? false;
  const formId = useId();

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Confirm it's you"
      subtitle={subtitle ?? "This action is sensitive — we verify identity first"}
      note="Required for security-sensitive domain actions."
      footer={
        <>
          <Button variant="secondary" size="lg" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="brand" size="lg" type="submit" form={formId} loading={verify.isPending}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <Form
        id={formId}
        className="flex flex-col gap-4"
        error={verify.error}
        onSubmit={(e) => {
          e.preventDefault();
          if (!verify.isPending) verify.mutate();
        }}
      >
        <Field size="lg" label="Enter your password" name="password" required>
          {(control) => (
            <PasswordInput
              control={CONTROL_LG}
              {...control}
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              // eslint-disable-next-line jsx-a11y/no-autofocus -- the form is this screen's only task, so focus starts in its first field (PHP-3348)
              autoFocus
            />
          )}
        </Field>
        {mfa && (
          <Field size="lg" label="2FA verification code" name="code" required>
            {(control) => (
              <Input
                size="lg"
                {...control}
                minLength={6}
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="6-digit code"
                maxLength={8}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\s/g, ""))}
              />
            )}
          </Field>
        )}
        {children ?? (
          <Hint tone="warning" title="Keep this private">
            Anyone with your domain's authorization code can move it away from CortexGrip.
          </Hint>
        )}
        <ErrorAlert error={verify.error} />
        {/* Hidden Enter-key submit trigger, not a visible action — not the shared Button. */}
        {/* eslint-disable-next-line portal/no-raw-button */}
        <button type="submit" hidden />
      </Form>
    </Modal>
  );
}

/** The person closed "Confirm it's you": nothing was changed. */
export class StepUpCancelled extends Error {
  constructor() {
    super("Cancelled — nothing was changed.");
    this.name = "StepUpCancelled";
  }
}

/**
 * Runs an action; if the API answers REAUTHENTICATION_REQUIRED it asks the person to confirm who
 * they are, then runs it again. Render `modal` once in the page.
 */
export function useStepUp() {
  const [pending, setPending] = useState<null | { label: string }>(null);
  const retry = useRef<null | (() => void)>(null);
  const cancel = useRef<null | (() => void)>(null);

  const run = useCallback(<T,>(action: () => Promise<T>, label = "Verify & continue"): Promise<T> => {
    return action().catch((err: unknown) => {
      if (!(err instanceof ApiError && err.reason === "REAUTHENTICATION_REQUIRED")) throw err;
      return new Promise<T>((resolve, reject) => {
        retry.current = () => {
          action().then(resolve, reject);
        };
        cancel.current = () => reject(new StepUpCancelled());
        setPending({ label });
      });
    });
  }, []);

  const modal = (
    <ConfirmIdentityModal
      open={pending !== null}
      confirmLabel={pending?.label}
      onClose={() => {
        setPending(null);
        cancel.current?.();
        retry.current = cancel.current = null;
      }}
      onConfirmed={() => {
        setPending(null);
        retry.current?.();
        retry.current = cancel.current = null;
      }}
    />
  );
  return { run, modal };
}
